import type { ComponentProps, FC, RefObject } from 'react';
import React, { createRef } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import type {
	ContentBlogUnifiedQueryType,
	ContentUnifiedQueryType,
} from '@confluence/content-unified-query';
import { PageContentRenderer } from '@confluence/content-renderer/entry-points/PageContentRenderer.ts';
import { DeepLinkHandler } from '@confluence/deep-link-handler';
import { HighlightActions } from '@confluence/highlight-actions/entry-points/HighlightActions.ts';
import { ScrollToHashLinkHandler } from '@confluence/scroll/entry-points/ScrollToHashLinkHandler.ts';
import {
	OnboardingNthUserContentTypeSpotlight,
	ContentType,
} from '@confluence/onboarding-nth-user-setup-modal/entry-points/OnboardingNthUserContentTypeSpotlight.tsx';
import { UNSAFE_isExperimentEnabledNoExposure } from '@confluence/feature-experiments';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { InlineActionsLoader } from './InlineActionsLoader/InlineActionsLoader.tsx';
import { useViewPageParams } from './useViewPageParams.ts';
import type {
	PageStatusQuery as PageStatusQueryType,
	PageStatusQueryVariables,
} from './graphql/__types__/PageStatusQuery.ts';
import { PageStatusQuery } from './graphql/PageStatusQuery.graphql.ts';
// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
const SPA = !process.env.REACT_SSR;

type PassThroughProps =
	| 'eventHandlerOverrides'
	| 'spaceKey'
	| 'error'
	| 'appearance'
	| 'hasInlineComments'
	| 'hasProgressiveRendering'
	| 'customPerfData';

type ViewPageContentRendererProps = Pick<
	ComponentProps<typeof PageContentRenderer>,
	PassThroughProps
> & {
	contentId: string;
	data?: ContentUnifiedQueryType | ContentBlogUnifiedQueryType;
	lastModifiedDate?: string;
	isArchived?: boolean;
	contentStatusError?: ApolloErrorInterop;
	isEmbeddedPage?: boolean;
	isSpaceOverview?: boolean;
};

const styles = cssMap({
	containerWidth: {
		width: '100%',
		margin: '0 auto',
	},
	footerSpacing: {
		marginBottom: token('space.1000'),
	},
});

const ViewPageContentRenderer: FC<ViewPageContentRendererProps> = ({
	contentId,
	data,
	isArchived,
	isEmbeddedPage,
	isSpaceOverview,
	hasProgressiveRendering = true,
	...passThroughProps
}) => {
	const { versionOverride, embeddedContentRender, queryHash } = useViewPageParams();

	const refOfNodeToWatchForChange: RefObject<HTMLDivElement> = createRef<HTMLDivElement>();

	return (
		<div
			id="content"
			data-inline-comments-target="true"
			{...(UNSAFE_isExperimentEnabledNoExposure('cc-maui-experiment')
				? { 'data-remix-main-content': 'true' }
				: {})}
			data-testid="page-content-only"
			// The "page view" classes are included for compatibility with Connect add-ons, see navigator-context.js in the Confluence Backend repo
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className="highlighter-context page view"
		>
			<Box
				xcss={cx(styles.containerWidth, !isSpaceOverview && styles.footerSpacing)}
				ref={refOfNodeToWatchForChange}
			>
				{SPA && refOfNodeToWatchForChange && queryHash ? (
					<DeepLinkHandler
						scrollTargetId={queryHash}
						refOfNodeToWatchForChange={refOfNodeToWatchForChange}
						scrollTarget="queryHash"
					/>
				) : null}

				{SPA && <ScrollToHashLinkHandler anchorContainer={refOfNodeToWatchForChange} />}
				<PageContentRenderer
					{...{
						contentId,
						versionOverride,
						embeddedContentRender,
						queryHash,
						data,
						isArchived,
						isEmbeddedPage,
						isSpaceOverview,
						hasProgressiveRendering,
					}}
					{...passThroughProps}
				/>
				<OnboardingNthUserContentTypeSpotlight
					contentType={ContentType.PAGE}
					contentId={contentId}
				/>
			</Box>
		</div>
	);
};

export type ViewPageContentProps = ViewPageContentRendererProps & {
	hasInlineActions?: boolean;
	disableTableOverflowShadow?: boolean;
};

export const ViewPageContent: FC<ViewPageContentProps> = ({
	contentId,
	hasInlineComments,
	hasInlineActions = true,
	data,
	lastModifiedDate,
	...passThroughProps
}) => {
	const { data: contentStatusData, error: contentStatusError } = useQueryInterop<
		PageStatusQueryType,
		PageStatusQueryVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-relay-graphql-operations -- Shared GraphQL operation, defined in ./graphql/PageStatusQuery.graphql.ts so the pact test and other consumers can share the same tagged node.
		PageStatusQuery,
		{
			variables: {
				contentId,
			},
		},
	);

	const isArchived =
		contentStatusData && String(contentStatusData?.content?.nodes?.[0]?.status) === 'archived';

	const isFabric = Boolean(
		data?.content?.nodes?.[0]?.body?.dynamic?.representation === 'atlas_doc_format',
	);

	const viewPageContentRenderer = (
		<>
			<ViewPageContentRenderer
				contentId={contentId}
				data={data}
				lastModifiedDate={lastModifiedDate}
				hasInlineComments={hasInlineComments}
				isArchived={isArchived}
				contentStatusError={contentStatusError}
				{...passThroughProps}
			/>
			{SPA && hasInlineActions && <InlineActionsLoader />}
		</>
	);

	// HighlightActions is only for TinyMCE
	if (isFabric) {
		return viewPageContentRenderer;
	}

	return (
		<HighlightActions
			contentId={contentId}
			contentType={ContentType.PAGE}
			hasInlineComments={hasInlineComments}
			lastModifiedDate={lastModifiedDate}
			isArchived={isArchived}
			contentStatusError={contentStatusError}
			isFabric={isFabric}
		>
			{viewPageContentRenderer}
		</HighlightActions>
	);
};
